import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { trackClick } from '@/lib/track';
import { TipWidget } from '@/components/tips/TipWidget';
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from '@/components/common/CoinPrice';
import { coinVars } from "@/lib/coins";
import { useJoinPrice, money } from "@/hooks/useJoinPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const priceLabel = money;

// Urgency line pool (fallback when CMS has none). One per page load, never back-to-back.
const HERO_URGENCY_POOL = [
  "The door's still open. For now.",
  'More people join every day.',
  'Everyone inside is already listening.',
  'The longer you wait, the higher the number gets.',
  'A new subscriber joins every time you overthink it.',
];
const pickRotating = (arr, key) => {
  if (!arr || !arr.length) return '';
  if (arr.length === 1) return arr[0];
  let last = -1;
  try { last = parseInt(localStorage.getItem(key) ?? '-1', 10); } catch {}
  let i = Math.floor(Math.random() * arr.length);
  if (i === last) i = (i + 1) % arr.length;
  try { localStorage.setItem(key, String(i)); } catch {}
  return arr[i];
};

// Chosen ONCE per page load and shared across every mount (hero + page bottom), so
// a visitor sees the same urgency line top and bottom on the same load.
let _urgencyCache = null;
export const useRotatingUrgency = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const [line, setLine] = useState(_urgencyCache);
  useEffect(() => {
    if (_urgencyCache !== null) { setLine(_urgencyCache); return; }
    let cancelled = false;
    const finalize = (lines) => {
      if (_urgencyCache === null) {
        const pool = (lines && lines.length) ? lines : HERO_URGENCY_POOL;
        _urgencyCache = pickRotating(pool, 'home_urgency_last');
      }
      if (!cancelled) setLine(_urgencyCache);
    };
    axios.get(`${API}/home/what-youre-missing`, { params: { lang } })
      .then((r) => finalize(r.data?.urgency_lines))
      .catch(() => finalize(null));
    return () => { cancelled = true; };
  }, [lang]);
  return line || _urgencyCache || '';
};

// Single source of truth for "is this person a current member" on public surfaces.
// Reads subscription_state (core/serialize.py) via PlatformAuthContext. Current,
// paused and past_due count as members (gifted/comped resolve to current there too).
// A narrow owner guard keeps the artist/admin from ever seeing a join pitch.
export const useIsMember = () => {
  const { subscriptionState, platformUser } = usePlatformAuth();
  const roles = platformUser?.roles || [];
  const isOwner = roles.includes('admin') || roles.includes('samtwo') || roles.includes('artist');
  return ['current', 'paused', 'past_due'].includes(subscriptionState) || isOwner;
};

// Admin CMS CTA copy (and its machine-translated locale variants) historically
// carry a trailing arrow glyph baked into the string, which overrides the i18n
// fallback. We never want an arrow on these buttons, so strip the glyph and ASCII
// arrow forms at the render boundary, in every language and whatever admin typed.
const stripArrows = (s) =>
  typeof s === 'string'
    ? s
        .replace(/[\u2192\u27a1\u2794\u279c\u2799\u279e\u2b95]/g, '')
        .replace(/\s*(?:->|-&gt;|=>|&rarr;)\s*/g, ' ')
        .replace(/[ \t]{2,}/g, ' ')
        .trim()
    : s;

// Shared CTA data: admin copy from /home/hero, live price from /settings/pricing
// with the Early Bird price override. One place, so admin edits reflect everywhere.
const useCtaData = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const { pricing, earlyBird, promos = {}, joinCoins: ctaCoins, joinDollars: ctaDollars } = useJoinPrice();
  const [hero, setHero] = useState(null);
  useEffect(() => {
    axios.get(`${API}/home/hero`, { params: { lang } }).then((r) => setHero(r.data)).catch(() => {});
  }, [lang]);
  const ctaPrice = t('coins.inlineWithDollar', { ...coinVars(ctaCoins), dollars: ctaDollars });
  const resolveCopy = (s) => (typeof s === 'string' ? s.replace(/\{\{\s*price\s*\}\}/g, ctaPrice) : s);
  return { pricing, earlyBird, promos, hero, ctaCoins, ctaPrice, resolveCopy };
};

// The join / subscribe button. variant "auto" mirrors the hero (subscribers get the
// go-to-music text); variant "join" always shows the join pitch (used where the
// audience swap already guaranteed a non-member, e.g. a former subscriber).
export const JoinButton = ({ source = 'hero', variant = 'auto', testId = 'join-cta', className = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMember = useIsMember();
  const { hero, ctaCoins, resolveCopy } = useCtaData();
  const asSubscriber = variant === 'auto' && isMember;
  // Join label is spliced through the shared CoinPrice at the {{price}} slot; CMS
  // copy and the fallback both carry the slot, so the treatment is inherited and
  // the surrounding words keep their locale order.
  const joinTemplate = stripArrows(hero?.primary_cta_text || t('home.hero.cta_join_fallback', { price: COIN_PRICE_SLOT }))
    .replace(/\{\{\s*price\s*\}\}/g, COIN_PRICE_SLOT);
  return (
    <button
      data-testid={testId}
      onClick={() => { if (!asSubscriber) trackClick('subscribe_cta', { source }); navigate(asSubscriber ? '/music' : '/pricing'); }}
      className={`pointer-events-auto btn-pill btn-primary text-sm sm:text-base ${className}`}
    >
      {asSubscriber
        ? stripArrows(hero?.subscriber_cta_text ? resolveCopy(hero.subscriber_cta_text) : t('home.hero.cta_subscriber_fallback'))
        : (ctaCoins != null && <CopyWithCoinPrice template={joinTemplate} coins={ctaCoins} size="sm" />)}
    </button>
  );
};

// The Boss early-bird / promo pill, identical markup and conditions to the hero.
export const JoinPill = () => {
  const { t } = useTranslation();
  const isMember = useIsMember();
  const { earlyBird, promos } = useCtaData();
  const bossPromo = promos.monthly ? { ...promos.monthly, key: 'monthly' }
    : (promos.annual ? { ...promos.annual, key: 'annual' } : null);
  const promoMonths = bossPromo?.duration_in_months || 1;
  const promoPillText = bossPromo && (
    (bossPromo.pill_text && bossPromo.pill_text.trim())
    || (bossPromo.key === 'annual'
      ? t('home.hero.promo_annual', { price: priceLabel(bossPromo.price) })
      : t(promoMonths === 1 ? 'home.hero.promo_monthly_one' : 'home.hero.promo_monthly_other', { price: priceLabel(bossPromo.price), count: promoMonths }))
  );
  // Early-bird follows the backend eligibility truth (a former member who never
  // claimed is eligible; one who consumed it is not). Never shown to a current
  // member. Promo pill is the non-member "become his boss" pitch.
  const showEbPill = !isMember && !!earlyBird?.active && !!earlyBird?.eligible;
  const showPromoPill = !isMember && !earlyBird?.active && !!bossPromo;
  // Early Bird pill hidden (owner decision 2026-11-02); promo pill logic unchanged.
  if (showEbPill) return null;
  if (showPromoPill) {
    return (
      <div className="mt-3">
        <span className="inline-block px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold" style={{ background: '#f5b301', color: '#1a1a1a' }} data-testid="hero-promo-banner">
          {promoPillText}
        </span>
      </div>
    );
  }
  return null;
};

// The full join block for page bottoms (home + blog): rotating urgency line, join
// button, and the pill, stacked. This is the ONLY copy of that block; the hero
// composes the same primitives in its own layout.
export const JoinCta = ({ source = 'home_footer' }) => {
  const urgency = useRotatingUrgency();
  return (
    <div data-testid="join-cta-block" className="flex flex-col items-center gap-4 text-center">
      {urgency && <p className="text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }} data-testid="join-cta-urgency">{urgency}</p>}
      <JoinButton source={source} variant="join" />
      <JoinPill />
    </div>
  );
};

export default JoinCta;

// Audience swap for page bottoms: current members (incl. paused, past_due, gifted,
// comped, and the owner) keep the tip widget with the SAME sourceType/sourceId so
// tip attribution and analytics are unchanged; everyone else (guest, never, former)
// gets the join block. One place, no duplicated swap logic.
export const JoinOrTip = ({ tipSourceType, tipSourceId, source = 'home_footer' }) => {
  const isMember = useIsMember();
  if (isMember) return <TipWidget sourceType={tipSourceType} sourceId={tipSourceId} />;
  return <JoinCta source={source} />;
};
